import { useMemo } from 'react';
import { Link } from 'react-router';
import cn from 'classnames';
import { SponsorsGridFormData } from './contracts';
import { Image, ImageVariant } from '../../../common/components/Image';
import { useSponsorsQuery } from '@data/sponsors/useSponsorsQuery';
import { SkeletonLoader } from '@ui/common/components/SkeletonLoader';

export type SponsorsGridProps = SponsorsGridFormData & {
  className?: string;
};

export function SponsorsGrid({
  title,
  showImage,
  showName,
  showDescription,
  showAllSponsors,
  sponsorIds,
  columns,
  className,
}: SponsorsGridProps) {
  const { data, isLoading, error } = useSponsorsQuery();

  const sponsors = useMemo(() => {
    if (!data) {
      return null;
    }

    return showAllSponsors ? data : data.filter((sponsor) => sponsorIds?.includes(sponsor.id));
  }, [showAllSponsors, sponsorIds, data]);

  if (isLoading) {
    return (
      <div style={{ display: 'block', width: '100%', height: '100%' }}>
        <SkeletonLoader count={21} />
      </div>
    );
  }

  if (!sponsors || error) {
    return null;
  }

  return (
    <div className={cn(className, 'sponsors-grid')}>
      {title && <h3 className={'sponsors-grid__title'}>{title}</h3>}
      <ul className={'sponsors-grid__sponsor-list'} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {sponsors.map((sponsor) => (
          <li key={sponsor.id} className={'sponsors-grid__sponsor-item'}>
            {sponsor.url && (
              <Link to={sponsor.url} className={'sponsors-grid__sponsor-link'} target="_blank">
                {showImage && sponsor.logo && (
                  <Image
                    variant={ImageVariant.Contain}
                    src={sponsor.logo}
                    alt={sponsor.name}
                    className={'sponsors-grid__sponsor-logo'}
                    style={{
                      objectFit: 'contain',
                    }}
                  />
                )}
              </Link>
            )}
            {!sponsor.url && showImage && sponsor.logo && (
              <Image
                src={sponsor.logo}
                alt={sponsor.name}
                className={'sponsors-grid__sponsor-logo'}
                style={{
                  objectFit: 'contain',
                }}
              />
            )}
            {showName && <h3 className={'sponsors-grid__sponsor-name'}>{sponsor.name}</h3>}
            {showDescription && <p className={'sponsors-grid__sponsor-description'}>{sponsor.description}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}
